import { STREAM } from "../constants";
import { v2, vDist, type Vec2 } from "../math";
import { bandWidth, nearestStream, streamPoint } from "./stream";
import type { RingFlow, WorldDef } from "./types";

/**
 * The flow at a point (blocks/s): how the rocks there are going, so that
 * whatever floats among them can go with them. Inside a stream's band
 * it's its belt (slower or faster across a bend, as its rocks are; the
 * nearest stream's, so in the gap between two side by side each belt's air
 * fades out on its own side, still air between); inside
 * a star's ring it's the ring turning round its star; fading out over
 * STREAM.flowEdge past either's edge; nothing anywhere else. The air goes
 * with the flow: a character or ship there steers and is slowed against it,
 * not still space (see movePlayer, applyShipControl).
 */
export function flowAt(world: Pick<WorldDef, "streams" | "rings">, p: Vec2): Vec2 {
  const near = nearestStream(world.streams, p);
  if (near) {
    const { route, s, o, d } = near;
    const k = fade(d - bandWidth(route, s) / 2);
    if (k > 0) {
      // Where a rock this far across the band is a block either way along it.
      const at = (x: number) => {
        const { p: q, dir } = streamPoint(route, x);
        return v2(q.x - dir.y * o, q.y + dir.x * o);
      };
      const a = at(s - 1);
      const b = at(s + 1);
      const scale = (STREAM.speed * k) / 2;
      return v2((b.x - a.x) * scale, (b.y - a.y) * scale);
    }
  }
  // The ring it's in, or nearest the edge of (in the lane between two that turn opposite ways, the nearer).
  let best: { r: RingFlow; k: number } | undefined;
  for (const r of world.rings ?? []) {
    const d = vDist(p, r.position);
    const k = fade(Math.max(r.r0 - d, d - r.r1));
    if (k > 0 && (!best || k > best.k)) best = { r, k };
  }
  if (!best) return v2();
  const { r, k } = best;
  return v2(-r.spin * (p.y - r.position.y) * k, r.spin * (p.x - r.position.x) * k);
}

/** All of the flow inside (`out` <= 0), none STREAM.flowEdge or more outside. */
function fade(out: number): number {
  return out <= 0 ? 1 : Math.max(0, 1 - out / STREAM.flowEdge);
}
